import { RequestStatus } from '@features/request/requestSlice';
import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { TableTopPanel } from 'components/TableTopPanel';
import { Toolbar } from 'components/Toolbar';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { Button, ChoiceGroup, cnMixSpace, IconAdd, Tabs } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { useCatalog } from '../CatalogList/hooks/useCatalog';
import type { TCatalogOtherAndLimitedId, TCatalogOtherAndLimitedTabItem } from '../Common/CatalogOtherAndLimited';
import {
    CatalogOtherAndLimitedTab,
    catalogOtherAndLimitedTabs,
    columnLabels,
    CreateOrEditCatalogCostModal,
    defaultSet,
    methodologyItems,
    useCatalogOtherAndLimitedAccess,
    useCatalogOtherAndLimitedColumns,
    useCatalogOtherAndLimitedData,
    useRemoveCatalogCost,
} from '../Common/CatalogOtherAndLimited';
import Styles from '../Common/CatalogOtherAndLimited/catalogOtherAndLimited.module.css';
import { CatalogTablePage } from '../Common/CatalogTablePage';

const methodologyChoiceItems = methodologyItems.map((item) => ({
    label: item.label,
    value: item.id,
}));

export const CatalogOtherCostsLimitedCosts = () => {
    const [methodology, setMethodology] = useState(ProjectMethodologyEnum.Uer);
    const [activeTab, setActiveTab] = useState<TCatalogOtherAndLimitedTabItem>(catalogOtherAndLimitedTabs[0]);
    const [currentItemId, setCurrentItemId] = useState<TCatalogOtherAndLimitedId>();
    const [isAdditionalTransportCreate, setIsAdditionalTransportCreate] = useState(false);
    const [nameFilter, setNameFilter] = useState<string[]>([]);

    const { id } = useParams();
    const { catalogData, fetchingStatus } = useCatalog(id);

    const { canCreate, canEdit, canDelete } = useCatalogOtherAndLimitedAccess();
    const tableRef = useRef<AgGridReact | null>(null);
    const tableApi = useMemo(() => (tableRef && 'current' in tableRef ? tableRef.current : undefined), [tableRef]);

    const labels = columnLabels[activeTab.id];
    const isOthersTab = activeTab.id === CatalogOtherAndLimitedTab.Others;

    const { items, rowData, totalCount, fetchedItems, getItemById, refetch } = useCatalogOtherAndLimitedData(
        activeTab.id,
        methodology,
        nameFilter,
    );

    const handleOpenCreateOrEditDialog = useCallback(
        (itemId: TCatalogOtherAndLimitedId, isAdditionalTransport = false) =>
            () => {
                setIsAdditionalTransportCreate(isAdditionalTransport);
                setCurrentItemId(itemId);
            },
        [],
    );

    const handleCloseCreateOrEditDialog = useCallback(() => {
        setCurrentItemId(undefined);
        setIsAdditionalTransportCreate(false);
    }, []);

    const { isOpenConfirmationDialog, handleOpenRemoveDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useRemoveCatalogCost({
            tab: activeTab.id,
            onSuccess: refetch,
        });

    const { columns, isColumnVisible, tableSettingsConfirm, fullSetSettingsColumns } = useCatalogOtherAndLimitedColumns(
        {
            tab: activeTab.id,
            canEdit,
            canDelete,
            items,
            onEdit: handleOpenCreateOrEditDialog,
            onRemove: handleOpenRemoveDialog,
            onNameFilterChange: setNameFilter,
        },
    );

    const onTabChange = useCallback(({ value }: { value: TCatalogOtherAndLimitedTabItem }) => {
        setActiveTab(value);
        setNameFilter([]);
        setCurrentItemId(undefined);
        setIsAdditionalTransportCreate(false);
    }, []);

    const methodologyValue = useMemo(
        () => methodologyChoiceItems.find((item) => item.value === methodology) ?? methodologyChoiceItems[0],
        [methodology],
    );

    const handleMethodologyChange = useCallback(({ value }: { value: (typeof methodologyChoiceItems)[number] }) => {
        setMethodology(value.value);
        setNameFilter([]);
        setCurrentItemId(undefined);
        setIsAdditionalTransportCreate(false);
    }, []);

    const currentItem = typeof currentItemId === 'number' ? getItemById(currentItemId) : undefined;
    const isAdditionalTransport =
        currentItemId === null ? isAdditionalTransportCreate : Boolean(currentItem?.isAdditionalTransport);

    return (
        <CatalogTablePage
            catalogName={catalogData?.data?.name}
            methodology={catalogData?.data?.type?.methodologies ?? []}
            isLoading={fetchingStatus === RequestStatus.FETCHING && !catalogData?.data?.name}
        >
            <Toolbar flex>
                <ChoiceGroup
                    className={cnMixSpace({ mL: 'l' })}
                    value={methodologyValue}
                    onChange={handleMethodologyChange}
                    size="s"
                    view="primary"
                    name="methodology"
                    items={methodologyChoiceItems}
                />
            </Toolbar>
            <div className={Styles.tabs}>
                <Tabs
                    value={activeTab}
                    onChange={onTabChange}
                    items={catalogOtherAndLimitedTabs}
                    getItemLabel={(item: TCatalogOtherAndLimitedTabItem) => item.label}
                    size="s"
                />
            </div>
            <div
                className={cn(
                    Styles.table,
                    cnMixSpace({
                        mT: 'l',
                        mH: 'xl',
                    }),
                )}
            >
                <div className={Styles.tableTop}>
                    <div className={Styles.tableTopActions}>
                        {isOthersTab ? (
                            <Button
                                label={labels.createButton}
                                view="secondary"
                                size="s"
                                iconLeft={IconAdd}
                                onClick={handleOpenCreateOrEditDialog(null)}
                                disabled={!canCreate}
                            />
                        ) : (
                            <>
                                <Button
                                    label={labels.createButton}
                                    view="secondary"
                                    size="s"
                                    iconLeft={IconAdd}
                                    onClick={handleOpenCreateOrEditDialog(null)}
                                    disabled={!canCreate}
                                />
                                <Button
                                    label={columnLabels[CatalogOtherAndLimitedTab.Limited].createTransportButton}
                                    view="secondary"
                                    size="s"
                                    iconLeft={IconAdd}
                                    onClick={handleOpenCreateOrEditDialog(null, true)}
                                    disabled={!canCreate}
                                />
                            </>
                        )}
                    </div>
                    <div className={Styles.tableTopPanel}>
                        <TableTopPanel
                            totalItems={totalCount}
                            fetchedItems={fetchedItems}
                            tableApi={tableApi}
                            optionalColumns={fullSetSettingsColumns}
                            isColumnVisible={isColumnVisible}
                            handleTableSettingsConfirm={tableSettingsConfirm}
                            showPinColumn
                        />
                    </div>
                </div>

                <AgGridTable
                    key={`${labels.tableUniqueId}-${methodology}`}
                    ref={tableRef}
                    rowData={rowData}
                    columnDefs={columns}
                    defaultColDef={defaultSet}
                    context={{
                        tableUniqueId: labels.tableUniqueId,
                    }}
                    getRowId={({ data }) => String(data.id)}
                    headerHeight={40}
                    suppressCellFocus
                    suppressRowTransform
                    animateRows
                />
            </div>

            {typeof currentItemId !== 'undefined' && (
                <CreateOrEditCatalogCostModal
                    id={currentItemId}
                    tab={activeTab.id}
                    methodology={methodology}
                    item={currentItem}
                    isAdditionalTransport={isAdditionalTransport}
                    onClose={handleCloseCreateOrEditDialog}
                    onSaveSuccess={refetch}
                />
            )}

            {isOpenConfirmationDialog && (
                <ConfirmationDialog
                    isOpened
                    onSubmit={handleActionQuery}
                    onClose={handleCloseConfirmationDialog}
                    textHeader={labels.removeHeader}
                    textConfirmation={labels.removeConfirmation}
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                />
            )}
        </CatalogTablePage>
    );
};

CatalogOtherCostsLimitedCosts.displayName = 'CatalogOtherCostsLimitedCosts';
